import { PaginationState } from '@tanstack/react-table'
import { useRolodexIds } from 'features/fde'
import { useRolodexEntriesCachedById } from 'features/knowledgeDocumentViewer/hooks/useRolodexEntriesCachedById'
import { type RolodexEntryQueryBody } from 'features/rolodex'
import { useMemo } from 'react'
import { RolodexLoadedEntryJson } from 'types'

export interface UsePaginatedRolodexEntriesOptions extends RolodexEntryQueryBody {
  pagination: PaginationState
}

export interface UsePaginatedRolodexEntriesReturn<T> {
  data: T[]
  totalCount: number
  allIds: readonly string[]
  isLoading: boolean
  isLoadingIds: boolean
  isLoadingEntries: boolean
  error: Error | null
  mutate: () => Promise<void>
}

export function usePaginatedRolodexEntries<T extends RolodexLoadedEntryJson>(
  loadOptions: UsePaginatedRolodexEntriesOptions
): UsePaginatedRolodexEntriesReturn<T> {
  const { pagination, ...baseLoadOptions } = loadOptions

  const idsQuery = useMemo(
    () => ({
      ...baseLoadOptions,
      limit: baseLoadOptions.limit ?? 100000,
    }),
    [baseLoadOptions]
  )

  const {
    ids: allIds,
    isLoading: isLoadingIds,
    error: idsError,
    forceRefresh: refreshIds,
  } = useRolodexIds(idsQuery)

  const totalCount = allIds?.length ?? 0

  const pageIds = useMemo(() => {
    if (!allIds) return []
    const { pageIndex, pageSize } = pagination
    return allIds.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize)
  }, [allIds, pagination])

  const {
    data: pageEntries,
    isLoading: isLoadingEntries,
    error: entriesError,
    mutate: mutateEntries,
  } = useRolodexEntriesCachedById<T>(pageIds)

  const sortedPageEntries = useMemo(() => {
    if (!pageEntries || pageIds.length === 0) return []
    const idToEntry = new Map<string, T>()
    for (const entry of pageEntries) {
      const id = entry?.entry?.id
      if (id) idToEntry.set(id, entry)
    }
    return pageIds
      .map((id) => idToEntry.get(id))
      .filter((entry): entry is T => entry !== undefined)
  }, [pageEntries, pageIds])

  const mutate = async () => {
    await refreshIds()
    await mutateEntries()
  }

  return {
    data: sortedPageEntries,
    totalCount,
    allIds: allIds ?? [],
    isLoading: isLoadingIds || isLoadingEntries,
    isLoadingIds,
    isLoadingEntries,
    error: idsError ?? entriesError ?? null,
    mutate,
  }
}
